import { FC } from 'react';

import { CivilLawSubjectType } from 'api/types/entity';
import { DialogConfirmRevokeOtpVerification } from 'components';
import { parseFullName } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import { ClientFormContent } from 'modules/client/common/components';
import { ActionProps } from 'modules/client/common/components/ClientFormContent/types';
import {
  LockAndUnlockReadOnlyProps,
  useLockAndUnlockReadOnly,
} from 'modules/client/common/hooks';
import { LegalClientType } from 'modules/client/legal/types';

import { useFormContext } from '../../../forms/legalRepresentativeForm';
import {
  LegalRepresentativeSections,
  LegalRepresentativeSectionsProps,
} from '../../LegalRepresentativeSections';

export interface FormContentProps
  extends LockAndUnlockReadOnlyProps,
    LegalRepresentativeSectionsProps {
  submitLoading?: boolean;
  revokeOtpVerificationLoading?: boolean;
  onSendInvitationToAccount?: () => void;
  onChangeClientType?: (v: LegalClientType) => void;
  onShowVersionHistory?: () => void;
  onRevokeOtpVerification?: () => Promise<any>;
}

export const FormContent: FC<FormContentProps> = ({
  submitLoading,
  revokeOtpVerificationLoading,
  lockClientIfPossible,
  onChangeClientType,
  unlockClient,
  onShowVersionHistory,
  onRevokeOtpVerification,
  onSendInvitationToAccount,
  ...sectionsProps
}) => {
  const { t } = useTranslation();
  const { resetForm, initialValues, setReadOnly } = useFormContext();

  const representativeFullName = parseFullName(initialValues.personalData);
  const representativeIsCompany =
    initialValues.roleInCompany.civilLawSubject?.value ===
    CivilLawSubjectType.JuridicalPerson;

  const headerTitle = t('client.legalRepresentative.title', {
    name: representativeIsCompany
      ? initialValues.companyName?.fullName
      : representativeFullName,
  });

  const { onDisableReadOnly, onEnableReadOnly } = useLockAndUnlockReadOnly(
    setReadOnly,
    { unlockClient, lockClientIfPossible }
  );

  const {
    isOpen: isOpenRevokeOtp,
    onClose: onCloseRevokeOtp,
    open: openRevokeOtp,
  } = useDialog();

  const onActionRevokeOtpVerification = () => {
    openRevokeOtp();
  };

  const onConfirmRevokeOtp = async () => {
    await onRevokeOtpVerification?.().finally(() => onCloseRevokeOtp());
  };

  const actionMenuProps: ActionProps = {
    onRevokeOtpVerification: onRevokeOtpVerification
      ? onActionRevokeOtpVerification
      : undefined,

    onSendInvitationToAccount,
  };

  return (
    <>
      <ClientFormContent
        shownTabBar
        actionMenuProps={actionMenuProps}
        content={<LegalRepresentativeSections {...sectionsProps} />}
        headerTitle={headerTitle}
        resetForm={resetForm}
        submitLoading={submitLoading}
        onEdit={onDisableReadOnly}
        onEnableReadOnly={onEnableReadOnly}
        onShowVersionHistory={onShowVersionHistory}
      />

      <DialogConfirmRevokeOtpVerification
        fullName={representativeFullName}
        isOpen={isOpenRevokeOtp}
        loading={revokeOtpVerificationLoading}
        onClose={onCloseRevokeOtp}
        onConfirm={onConfirmRevokeOtp}
      />
    </>
  );
};
